import classNames from 'classnames';
import {
  type ComponentProps,
  type CSSProperties,
  type MouseEventHandler,
  useMemo,
  useRef,
} from 'react';

import { getOtherColor, getPrimaryColor, NoData } from '@revelio/replots';

import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import { GlobalAiChartPreviewDataReporter } from '../../../../global-ai-assistant/global-ai-assistant-chart-preview-data-reporter';
import {
  GLOBAL_AI_INLINE_CHART_METRIC_PLOT_HEIGHT,
  GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT,
} from '../../../constants/panelWidths';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import {
  DataProvider,
  MultiMetricDataProvider,
} from '../../DataProvider/DataProvider';
import type { MultiMetricProviderResult } from '../../DataProvider/MultiMetricProvider';
import { ResolvedDataProvider } from '../../DataProvider/ResolvedDataProvider';
import { SnowflakeDataProvider } from '../../DataProvider/SnowflakeDataProvider';
import {
  DataProviderQueryStatus,
  type DataProviderResult,
  type DataProviderSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../../DataProvider/types';
import { isComputedFromComposition } from '../../DataResolver/composition-inputs';
import type { ResolvedData } from '../../DataResolver/resolve-items';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import { usePageState } from '../../PageState/PageStateProvider';
import { isSingleEntityChart } from '../../PageState/utils/is-single-entity-chart';
import { ChartCardShell } from '../ChartCardShell';
import { ChartError } from '../ChartError';
import {
  ChartActionMenu,
  ChartHeader,
  ChartHeaderAction,
  ChartHeaderTitleInfo,
  ChartSwitchers,
} from '../ChartHeader';
import { ChartHeaderBreadcrumbProvider } from '../ChartHeaderBreadcrumb';
import { ChartLoader } from '../ChartLoader';
import { isChartLoadingFromSwitcher } from '../hooks/useChartSwitcherPlot';
import { isParameterSelectionEqualToDataPoint } from '../utils/check-datapoint-in-parameter';
import { getParameterAggregatedData } from '../utils/get-parameter-aggregated-data';
import { getPrimaryParameterSelections } from '../utils/get-primary-parameter-selections';
import { groupAndFilterData } from '../utils/group-and-filter-data';
import { transformComparisonData } from '../utils/transform-comparison-data';
import { ChartCard } from './Chart';
import cardStyles from './Chart.module.css';
import { ChartSelector, MultiMetricBodyChartSelector } from './ChartSelector';
import { getResolvedDataSetType } from './can-draw-resolved-data';
import { classifyChartDataRoute } from './classify-chart-data-route';
import styles from './data-wrapped-chart.module.css';

type PickedDataChartProps = Pick<
  DataChartProps,
  | 'item'
  | 'headerSize'
  | 'chartType'
  | 'chartTitle'
  | 'primaryFilterId'
  | 'secondaryDimension'
  | 'metric'
  | 'pageId'
  | 'onClick'
>;

type DataWrappedChartProps = PickedDataChartProps & {
  editMode?: boolean;
  onLoading?: (loading: boolean) => void;
  data?: ResolvedData;
};

const ChartNoDataState = () => (
  <div className={styles.noDataState}>
    <NoData />
  </div>
);

export const DataWrappedChart = ({
  item,
  chartType,
  headerSize,
  chartTitle,
  primaryFilterId,
  secondaryDimension,
  metric,
  editMode,
  pageId,
  onClick,
  onLoading,
  data,
}: DataWrappedChartProps) => {
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const { pageState } = usePageState();
  const loaderContainedInChart =
    editMode ||
    isGlobalAiChartPreview ||
    isChartLoadingFromSwitcher(item.id, pageState.chartSwitcherPlotId);

  // Shared with ChartRootSelector and the assistant's headless digest router:
  // the provider choice below must never drift from the headless mirror.
  const route = classifyChartDataRoute(item);
  const snowflakeConfig = item.plot?.snowflakeConfig;

  // route === 'snowflake' implies the config is set (the classifier checked
  // it); the extra condition only narrows the type for the provider prop.
  if (route === 'snowflake' && snowflakeConfig) {
    return (
      <div
        className={styles.chartFrame}
        data-loader-contained={loaderContainedInChart || undefined}
      >
        <SnowflakeDataProvider
          config={snowflakeConfig}
          chartType={chartType}
          defaultColor={chartType === 'bar' ? item.plot?.color : undefined}
        >
          {({ data, error, status }) => (
            <>
              {/* Always mount — also feeds the custom-page narration collector
                  on real pages (see the main-branch note below). */}
              <GlobalAiChartPreviewDataReporter
                itemId={item.id}
                branch="snowflake"
                status={status}
                error={error}
                data={data}
                chartType={chartType}
                metric={metric}
                secondaryDimension={item.plot?.secondaryDimension}
                displayAs={item.plot?.displayAs}
              />
              {(() => {
                if (status === DataProviderQueryStatus.Error) {
                  return (
                    <ChartCardShell onClick={onClick} item={item}>
                      <ChartError />
                    </ChartCardShell>
                  );
                }

                onLoading?.(status === DataProviderQueryStatus.Fetching);

                if (status === DataProviderQueryStatus.Fetching) {
                  return (
                    <ChartCardShell onClick={onClick} item={item}>
                      <ChartLoader onClick={onClick} />
                    </ChartCardShell>
                  );
                }

                if (!data) {
                  return (
                    <ChartCardShell onClick={onClick} item={item}>
                      <ChartNoDataState />
                    </ChartCardShell>
                  );
                }

                return (
                  <DataChart
                    item={item}
                    chartType={chartType}
                    headerSize={headerSize}
                    chartTitle={chartTitle}
                    primaryFilterId={primaryFilterId}
                    secondaryDimension={secondaryDimension}
                    metric={metric}
                    data={data}
                    dataType={data.type}
                    pageId={pageId}
                    onClick={onClick}
                  />
                );
              })()}
            </>
          )}
        </SnowflakeDataProvider>
      </div>
    );
  }

  if (route === 'multi-metric') {
    return (
      <div
        className={styles.chartFrame}
        data-loader-contained={loaderContainedInChart || undefined}
      >
        <MultiMetricDataProvider
          item={item}
          chartType={chartType}
          primaryDimension={primaryFilterId}
          resolved={data}
          onClick={onClick}
        >
          {({ data, error, status }) => (
            <>
              {/* Always mount — also feeds the custom-page narration collector
                  on real pages (see the main-branch note below). */}
              <GlobalAiChartPreviewDataReporter
                itemId={item.id}
                branch="multi"
                status={status}
                error={error}
                data={data}
                chartType={chartType}
                metric={metric}
                secondaryDimension={item.plot?.secondaryDimension}
                displayAs={item.plot?.displayAs}
              />
              {(() => {
                if (status === DataProviderQueryStatus.Error) {
                  return (
                    <ChartCardShell onClick={onClick} item={item}>
                      <ChartError />
                    </ChartCardShell>
                  );
                }

                onLoading?.(status === DataProviderQueryStatus.Fetching);

                if (status === DataProviderQueryStatus.Fetching) {
                  return (
                    <ChartCardShell onClick={onClick} item={item}>
                      <ChartLoader onClick={onClick} />
                    </ChartCardShell>
                  );
                }

                if (!data) {
                  return (
                    <ChartCardShell onClick={onClick} item={item}>
                      <ChartNoDataState />
                    </ChartCardShell>
                  );
                }

                return (
                  <MultiMetricDataChart
                    item={item}
                    chartType={chartType}
                    headerSize={headerSize}
                    chartTitle={chartTitle}
                    primaryFilterId={primaryFilterId}
                    metric={metric}
                    data={data}
                    onClick={onClick}
                  />
                );
              })()}
            </>
          )}
        </MultiMetricDataProvider>
      </div>
    );
  }

  // 'main' — 'table'/'special' can't occur here because ChartRootSelector
  // routes them away before this component mounts.
  const resolvedDataType = getResolvedDataSetType(item, metric);
  const renderMainChart = ({
    data,
    error,
    status,
  }: DataProviderResult<DataProviderSet>) => (
    <>
      {/* Mounted unconditionally: besides the GA chart-preview overlay,
          this reporter feeds the custom-page NarrationCollector on REAL
          pages — which have no gaChartPreviewMeta. Gating it on the preview
          meta starved the collector (0 digests), so create_custom_page
          narrated the page blind. The component self-guards both effects
          (preview needs meta; collection needs an active collector), so
          always mounting is safe and matches TableChart. */}
      <GlobalAiChartPreviewDataReporter
        itemId={item.id}
        branch="main"
        status={status}
        error={error}
        data={data}
        chartType={chartType}
        metric={metric}
        secondaryDimension={item.plot?.secondaryDimension}
        displayAs={item.plot?.displayAs}
      />
      {(() => {
        if (status === DataProviderQueryStatus.Error) {
          return (
            <ChartCardShell onClick={onClick} item={item}>
              <ChartError />
            </ChartCardShell>
          );
        }

        onLoading?.(status === DataProviderQueryStatus.Fetching);

        if (status === DataProviderQueryStatus.Fetching) {
          return (
            <ChartCardShell onClick={onClick} item={item}>
              <ChartLoader onClick={onClick} />
            </ChartCardShell>
          );
        }

        if (!data) {
          return (
            <ChartCardShell onClick={onClick} item={item}>
              <ChartNoDataState />
            </ChartCardShell>
          );
        }

        return (
          <DataChart
            item={item}
            chartType={chartType}
            headerSize={headerSize}
            chartTitle={chartTitle}
            primaryFilterId={primaryFilterId}
            secondaryDimension={secondaryDimension}
            metric={metric}
            data={data}
            dataType={data.type}
            pageId={pageId}
            onClick={onClick}
          />
        );
      })()}
    </>
  );

  return (
    <div
      className={styles.chartFrame}
      data-loader-contained={loaderContainedInChart || undefined}
    >
      {data &&
      resolvedDataType &&
      !(metric && isComputedFromComposition(metric)) ? (
        <ResolvedDataProvider
          resolved={data}
          dataType={resolvedDataType}
          metric={metric}
          onClick={onClick}
        >
          {renderMainChart}
        </ResolvedDataProvider>
      ) : (
        <DataProvider
          chartType={chartType}
          primaryDimension={primaryFilterId}
          secondaryDimension={secondaryDimension}
          metric={metric}
          resolved={data}
          onClick={onClick}
        >
          {renderMainChart}
        </DataProvider>
      )}
    </div>
  );
};

type ChartActionMenuProps = Pick<
  ComponentProps<typeof ChartActionMenu>,
  'primaryFilterId' | 'dataType'
>;

type BodyChartSelectorProps = Pick<
  ComponentProps<typeof ChartSelector>,
  | 'chartType'
  | 'item'
  | 'metric'
  | 'secondaryDimension'
  | 'pageId'
  | 'primaryParameterValues'
>;

type DataChartProps = ChartActionMenuProps &
  BodyChartSelectorProps & {
    data: DataProviderSet;
    dataType: DataType;
    headerSize?: 'sm' | 'md';
    hideActionMenu?: boolean;
    chartTitle: string;
    onClick?: MouseEventHandler<HTMLDivElement>;
  };
const DataChart = ({
  item,
  chartType,
  headerSize,
  hideActionMenu = false,
  chartTitle,
  primaryFilterId,
  secondaryDimension,
  metric,
  data,
  dataType,
  pageId,
  onClick,
}: DataChartProps) => {
  const printRef = useRef<HTMLDivElement>(null);
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const showActionMenu = !hideActionMenu && !isGlobalAiChartPreview;
  const showPrimaryEntitySwitcher = isSingleEntityChart(item);
  const { activePrimaryFilterLength } = usePrimaryFilterState(primaryFilterId);

  const { config: tabContentConfig } = useTabContentConfig();
  const { activeFilterMap } = useFilterContext();

  const rawReportParameters = tabContentConfig?.reportParameters;
  const reportParameters = useMemo(
    () => rawReportParameters ?? [],
    [rawReportParameters]
  );

  const plotParameters = item?.plot?.plotParameters;
  const aggregatedData = useMemo(
    () =>
      getParameterAggregatedData({
        data,
        plotParameters: plotParameters ?? [],
        reportParameters,
        activeFilterMap,
      }),
    [data, plotParameters, reportParameters, activeFilterMap]
  );

  const comparisonTransformation = item?.plot?.comparisonTransformation;
  const shouldApplyComparisonTransformation =
    chartType !== 'diverging-bar-combo' && !!comparisonTransformation;

  const transformedData = useMemo(() => {
    if (shouldApplyComparisonTransformation && comparisonTransformation) {
      return transformComparisonData(aggregatedData, comparisonTransformation);
    }
    return aggregatedData;
  }, [
    aggregatedData,
    shouldApplyComparisonTransformation,
    comparisonTransformation,
  ]);

  const { groupBySegment, plotFilters, hideOther } = item?.plot ?? {};
  const filteredData = useMemo(
    () =>
      groupAndFilterData({
        data: transformedData,
        groupBySegment,
        plotFilters,
        hideOther,
      }),
    [transformedData, groupBySegment, plotFilters, hideOther]
  );
  const gaCompactMetricPreview =
    isGlobalAiChartPreview &&
    activePrimaryFilterLength === 1 &&
    chartType === 'bar' &&
    filteredData?.type === DataType.SINGLE &&
    filteredData.data.length === 1;
  const gaInlinePlotHeight = gaCompactMetricPreview
    ? GLOBAL_AI_INLINE_CHART_METRIC_PLOT_HEIGHT
    : GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT;

  const isCompetitorBarChart =
    chartType === 'bar' && item?.plot?.dimensionSource === 'competitors';
  const competitorPrimaryFilterId = isCompetitorBarChart
    ? tabContentConfig?.primaryFilter?.id
    : undefined;
  const primaryParameterValues = useMemo(
    () =>
      getPrimaryParameterSelections({
        reportParameters,
        activeFilterMap,
        primaryFilterId: competitorPrimaryFilterId,
      }),
    [reportParameters, activeFilterMap, competitorPrimaryFilterId]
  );
  const chartSelectorData = useMemo(
    () =>
      chartType === 'bar' &&
      filteredData?.type === DataType.SINGLE &&
      primaryParameterValues.length > 0
        ? ({
            ...filteredData,
            data: filteredData.data.map((point) => ({
              ...point,
              color:
                point.color ??
                (primaryParameterValues.some((p) =>
                  isParameterSelectionEqualToDataPoint(
                    p,
                    point as SingleDataPoint
                  )
                )
                  ? getPrimaryColor()
                  : getOtherColor()),
            })),
          } as SingleDataSet)
        : filteredData,
    [chartType, filteredData, primaryParameterValues]
  );

  // Memoized so a page-config re-render (e.g. resizing a different chart on
  // the page) yields the same element reference and React skips reconciling the
  // whole chart body
  const ChartRender = useMemo(
    () => (
      <ChartSelector
        chartType={chartType}
        data={chartSelectorData}
        item={item}
        metric={metric}
        secondaryDimension={secondaryDimension}
        pageId={pageId}
        primaryParameterValues={primaryParameterValues}
      />
    ),
    [
      chartType,
      chartSelectorData,
      item,
      metric,
      secondaryDimension,
      pageId,
      primaryParameterValues,
    ]
  );

  const titleCentered = item?.isTitleCentered === true;

  const chartBodyContent = isGlobalAiChartPreview ? (
    <div
      className={styles.plotArea}
      data-compact-metric={gaCompactMetricPreview || undefined}
      style={{ '--plot-height': gaInlinePlotHeight } as CSSProperties}
    >
      {ChartRender}
    </div>
  ) : (
    ChartRender
  );

  return (
    <ChartHeaderBreadcrumbProvider>
      <ChartCard onClick={onClick}>
        <ChartHeader hasActionMenu={showActionMenu} isCentered={titleCentered}>
          <ChartHeaderTitleInfo info={item?.info} title={chartTitle} />
          <div
            className={styles.headerSwitchers}
            data-centered={titleCentered || undefined}
          >
            <ChartHeaderAction>
              <ChartSwitchers
                primaryFilterId={primaryFilterId}
                isSingleEntityChart={showPrimaryEntitySwitcher}
                hasActionMenu={showActionMenu}
              />
              {showActionMenu && (
                <ChartActionMenu
                  printRef={printRef}
                  item={item}
                  metric={metric}
                  title={chartTitle}
                  primaryFilterId={primaryFilterId}
                  slim={headerSize === 'sm'}
                  data={transformedData}
                  dataType={
                    shouldApplyComparisonTransformation
                      ? transformedData.type
                      : dataType
                  }
                  modalContent={ChartRender}
                />
              )}
            </ChartHeaderAction>
          </div>
        </ChartHeader>
        <div
          ref={printRef}
          data-testid="card-body"
          data-ga-preview={isGlobalAiChartPreview || undefined}
          className={classNames(styles.chartBody, cardStyles.cardBody)}
        >
          {chartBodyContent}
        </div>
      </ChartCard>
    </ChartHeaderBreadcrumbProvider>
  );
};

type MultiMetricDataChartProps = Pick<
  DataChartProps,
  | 'item'
  | 'chartType'
  | 'primaryFilterId'
  | 'chartTitle'
  | 'headerSize'
  | 'hideActionMenu'
  | 'onClick'
> & {
  data: MultiMetricProviderResult;
  secondaryDimension?: DataChartProps['secondaryDimension'];
  metric?: DataChartProps['metric'];
};
const MultiMetricDataChart = ({
  item,
  chartType,
  primaryFilterId,
  chartTitle,
  data,
  headerSize,
  hideActionMenu,
  onClick,
  metric,
}: MultiMetricDataChartProps) => {
  const printRef = useRef<HTMLDivElement>(null);
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const showActionMenu = !hideActionMenu && !isGlobalAiChartPreview;
  const showPrimaryEntitySwitcher = isSingleEntityChart(item);
  const { config: tabContentConfig } = useTabContentConfig();
  const { activeFilterMap } = useFilterContext();
  const rawReportParameters = tabContentConfig?.reportParameters;
  const plotParameters = item.plot?.plotParameters;

  const exportSeriesForMenu = useMemo(() => {
    const defaultMetric = item.plot?.defaultMetricOptions?.[0];
    const series =
      (defaultMetric !== undefined
        ? data.byMetric[defaultMetric]
        : undefined) ??
      (
        Object.entries(data.byMetric) as [
          MetricValues,
          DataProviderSet | undefined,
        ][]
      ).find(([, entry]) => entry !== undefined)?.[1] ??
      data.fallback;
    return series
      ? getParameterAggregatedData({
          data: series,
          plotParameters: plotParameters ?? [],
          reportParameters: rawReportParameters ?? [],
          activeFilterMap,
        })
      : series;
  }, [
    data,
    item.plot?.defaultMetricOptions,
    plotParameters,
    rawReportParameters,
    activeFilterMap,
  ]);

  const ChartRender = (
    <MultiMetricBodyChartSelector
      item={item}
      chartType={chartType}
      data={data}
      metric={metric}
    />
  );

  const chartBodyContent = isGlobalAiChartPreview ? (
    <div
      className={styles.plotArea}
      style={
        {
          '--plot-height': GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT,
        } as CSSProperties
      }
    >
      {ChartRender}
    </div>
  ) : (
    ChartRender
  );

  return (
    <ChartCard onClick={onClick}>
      <ChartHeader hasActionMenu={showActionMenu}>
        <ChartHeaderTitleInfo info={item?.info} title={chartTitle} />
        <div className={styles.headerSwitchers}>
          <ChartHeaderAction>
            <ChartSwitchers
              primaryFilterId={primaryFilterId}
              isSingleEntityChart={showPrimaryEntitySwitcher}
              hasActionMenu={showActionMenu}
            />
            {showActionMenu && (
              <ChartActionMenu
                printRef={printRef}
                item={item}
                title={chartTitle}
                primaryFilterId={primaryFilterId}
                slim={headerSize === 'sm'}
                data={exportSeriesForMenu ?? data}
                dataType={exportSeriesForMenu?.type}
                modalContent={ChartRender}
              />
            )}
          </ChartHeaderAction>
        </div>
      </ChartHeader>
      <div
        ref={printRef}
        data-testid="card-body"
        data-ga-preview={isGlobalAiChartPreview || undefined}
        className={classNames(styles.chartBody, cardStyles.cardBody)}
      >
        {chartBodyContent}
      </div>
    </ChartCard>
  );
};
